import {
  validatePageQuery,
  validatePerPageQuery,
  initialPageFilterState,
  handleErrorMessage,
  isTruthy,
  debounceEventHandler,
} from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { pageFilters, Sender } from "models/interfaces";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchAllSender, fetchAllSenderCount } from "./SendersService";
import {
  Stack,
  Tooltip,
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { getSenderStatus } from "screens/Contacts/ContactsConstants";
import CustomTable from "global/components/CustomTable/CustomTable";
import AddUpdateSender from "./Components/AddUpdateSender";
import SenderVerification from "./Components/SenderVerification";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";
import VerifySender from "assets/icons1/Verify_Sender.svg";
import EditIcon from "assets/icons1/EditIcon.svg";
import RemoveIcon from "../../../assets/icons1/remove_icon.svg";
import SenderDeleteModal from "./Components/SenderDeleteModal";
import strings from "global/constants/StringConstants";
import SearchIcon from "assets/icons1/search_icon.svg";

const getColors = {
  0: "#475569",
  1: "#47AA12", // Verified
} as { [key: number]: string };

const senderHeader = [
  {
    name: "Display name",
    field: "name",
  },
  {
    name: "Email",
    field: "email",
  },
  {
    name: "Status",
    field: "status",
  },
  {
    name: "Action",
    field: "action",
  },
];

const Senders = () => {
  const [senders, setSenders] = useState<string[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editSenderData, setEditSenderData] = useState<{
    id: number;
    name: string;
    email: string;
  } | null>(null);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");
  const [sendersFilters, setSendersFilters] = useState<pageFilters>(
    initialPageFilterState(pageFromUrl, rowsPerPageFromUrl, searchFromUrl ?? "")
  );
  const [openVerificationModal, setOpenVerificationModal] =
    useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openedFrom, setOpenedFrom] = useState<"add" | "verify">("verify");

  const [verificationDetails, setVerificationDetails] = useState({
    senderId: 0,
    senderName: "",
    senderEmail: "",
  });

  useEffect(() => {
    const { page, rowsPerPage, searchText } = sendersFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?tab=senders&page=${page}&rowsPerPage=${rowsPerPage}`
    );
    getTableData();
  }, [sendersFilters]);

  const getTableData = async () => {
    const { searchText, page, rowsPerPage } = sendersFilters;
    try {
      setIsLoading(true);
      const count = await fetchAllSenderCount(searchText);
      setCount(count);
      await getTableDataAccordingToPage(
        count,
        page,
        searchText.trim(),
        rowsPerPage
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    count: number,
    page: number,
    searchText: string,
    rowsPerPage: number
  ) => {
    try {
      let senderRes: any[] = [];
      senderRes = await fetchAllSender(page, rowsPerPage, searchText, []);
      const tableData = convertDataToTableFormat(senderRes);
      setSenders([...tableData]);
    } catch (error: any) {
      throw error;
    }
  };

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setSendersFilters({
      ...sendersFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setSendersFilters({
      ...sendersFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setSendersFilters({
      ...sendersFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const handleSenderAdded = (
    senderId: number,
    senderName: string,
    senderEmail: string
  ) => {
    setOpenedFrom("add");
    setVerificationDetails({
      senderId: senderId,
      senderName: senderName,
      senderEmail: senderEmail,
    });
    setOpenVerificationModal(true);
  };

  const handleVerifySender = (sender: Sender) => {
    setOpenedFrom("verify");
    setVerificationDetails({
      senderId: sender.id || 0,
      senderName: sender.name,
      senderEmail: sender.email,
    });
    setOpenVerificationModal(true);
  };

  const handleEditSender = (sender: Sender) => {
    setEditSenderData({
      id: sender.id || 0,
      name: sender.name,
      email: sender.email,
    });
    setIsEditMode(true);
    setOpen(true);
  };

  const handleAddSender = () => {
    setEditSenderData(null);
    setIsEditMode(false);
    setOpen(true);
  };

  const handleDeleteSender = (sender: Sender) => {
    setVerificationDetails({
      senderId: sender.id || 0,
      senderName: sender.name,
      senderEmail: sender.email,
    });
    setOpenDeleteModal(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
    setEditSenderData(null);
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((sender: any) => {
      return {
        rowId: sender.id,
        email: (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography color="text.primary" variant="subtitle2">
              {sender.email}
            </Typography>
            {!sender.domainVerified && (
              <CustomInfoTooltip infoMessage="The sender's domain has not been authenticated. go to domains to add or update the domain to improve email deliverability.">
                <Chip
                  label="Domain not authenticated"
                  sx={{
                    borderRadius: "5px",
                  }}
                  size="medium"
                />
              </CustomInfoTooltip>
            )}
          </Stack>
        ),
        name: (
          <Tooltip
            title={sender.name ? sender.name : "N/A"}
            arrow
            placement="top-start"
          >
            <Typography sx={{ color: "#475569" }} variant="subtitle2">
              {sender.name
                ? sender.name.length > 20
                  ? `${sender.name.slice(0, 20)}...`
                  : sender.name
                : "N/A"}
            </Typography>
          </Tooltip>
        ),
        status: (
          <Typography
            sx={{
              color: getColors[sender.status],
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                borderRadius: "50%",
                height: 6,
                width: 6,
                marginRight: 1,
                backgroundColor: getColors[sender.status],
              }}
            />
            {getSenderStatus(sender.status)}
          </Typography>
        ),
        action: (
          <Stack direction="row" spacing={2}>
            {sender.status === 1 ? (
              <Stack direction="row" spacing={2}>
                <CustomInfoTooltip placement="bottom" infoMessage={"Edit"}>
                  <IconButton
                    size="medium"
                    sx={{
                      color: "#fff",
                      padding: 0,
                      cursor: "pointer",
                    }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleEditSender(sender);
                    }}
                  >
                    <img src={EditIcon} width="25px" alt="edit_icon" />
                  </IconButton>
                </CustomInfoTooltip>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2}>
                <CustomInfoTooltip
                  placement="bottom"
                  infoMessage={"Verify sender"}
                >
                  <IconButton
                    size="medium"
                    sx={{
                      color: "#fff",
                      padding: 0,
                      cursor: "pointer",
                    }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleVerifySender(sender);
                    }}
                  >
                    <img src={VerifySender} width="25px" alt="verify_icon" />
                  </IconButton>
                </CustomInfoTooltip>
              </Stack>
            )}
            <CustomInfoTooltip placement="bottom" infoMessage={"Delete sender"}>
              <IconButton
                size="medium"
                sx={{
                  color: "#fff",
                  padding: 0,
                  cursor: "pointer",
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  handleDeleteSender(sender);
                }}
              >
                <img src={RemoveIcon} alt="verify_icon" />
              </IconButton>
            </CustomInfoTooltip>
          </Stack>
        ),
      };
    });
  };

  const getSenderHeader = () => {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Card
          sx={{
            flex: 1,
            paddingRight: 0,
          }}
        >
          <CardContent
            sx={{
              padding: 0,
              "&:last-child": {
                paddingBottom: "0px",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <TextField
                id="senders_search_box"
                placeholder="Search Senders"
                variant="outlined"
                size="small"
                name="search"
                sx={{
                  flex: 1,
                  height: "40px",
                  "& .MuiOutlinedInput-root": {
                    height: "100%",
                    paddingRight: 0,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none !important",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none !important",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none !important",
                  },
                  "&.MuiOutlinedInput-root.Mui-focused": {
                    border: "none !important",
                  },
                }}
                autoComplete="off"
                onChange={debounceEventHandler(
                  searchChangeHandler,
                  strings.SEARCH_TIME_OUT
                )}
                inputRef={searchTerm}
                InputProps={{
                  startAdornment: (
                    <Stack mr={1}>
                      <img src={SearchIcon} width="20px" height="20px" />
                    </Stack>
                  ),
                }}
              />
            </Stack>
          </CardContent>
        </Card>
        <Button variant="contained" size="small" onClick={handleAddSender}>
          Add sender
        </Button>
      </Stack>
    );
  };

  const getSendersList = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Box>
          <CustomTable
            headers={senderHeader}
            rows={senders}
            page={sendersFilters.page}
            rowsPerPage={sendersFilters.rowsPerPage}
            onRowsPerPageChange={perPageRowsChangeHandler}
            onPageChange={pageChangeHandler}
            count={count}
            showJumpToPageNumber={false}
            handleOnChangePageInput={pageChangeHandler}
            showPagination={true}
            isLoading={isLoading}
          />
        </Box>
      </CenteredCardLayout>
    );
  };

  const handleVerificationModalClose = () => {
    setOpenVerificationModal(false);
    setVerificationDetails({
      senderId: 0,
      senderName: "",
      senderEmail: "",
    });
  };

  const handleDeleteModalClose = () => {
    setOpenDeleteModal(false);
    setVerificationDetails({
      senderId: 0,
      senderName: "",
      senderEmail: "",
    });
  };

  const getSenderPage = () => {
    return (
      <>
        <Stack direction="column" spacing={2}>
          {getSenderHeader()}
          {getSendersList()}
        </Stack>

        {open && (
          <AddUpdateSender
            isVisible={open}
            handleDialogClose={handleClose}
            filters={sendersFilters}
            setFilters={setSendersFilters}
            onSenderAdded={handleSenderAdded}
            isEdit={isEditMode}
            editSenderData={editSenderData || undefined}
          />
        )}

        {openVerificationModal && (
          <SenderVerification
            isVisible={openVerificationModal}
            handleDialogClose={handleVerificationModalClose}
            filters={sendersFilters}
            setFilters={setSendersFilters}
            senderEmail={verificationDetails.senderEmail}
            senderName={verificationDetails.senderName}
            senderId={verificationDetails.senderId}
            openedFrom={openedFrom}
          />
        )}

        {openDeleteModal && (
          <SenderDeleteModal
            isVisible={openDeleteModal}
            handleDialogClose={handleDeleteModalClose}
            filters={sendersFilters}
            setFilters={setSendersFilters}
            verificationDetails={verificationDetails}
          />
        )}
      </>
    );
  };

  return <>{getSenderPage()}</>;
};

export default Senders;
